import { COLOR_VARS } from "@/lib/boo-colors";
import { useEffect, useState, type CSSProperties } from "react";

function pick(exclude: readonly string[] = []): string {
  // Light loop to avoid picking the same color that's already on screen.
  while (true) {
    const c = COLOR_VARS[Math.floor(Math.random() * COLOR_VARS.length)];
    if (!exclude.includes(c)) return c;
  }
}

const SWAP_INTERVAL_MS = 2200;

/**
 * Hook that returns inline-style values for the shared Boo gradient — a
 * 3-stop linear gradient whose color stops rotate every
 * {@link SWAP_INTERVAL_MS}. Both `BooLogo` (the blob mark) and
 * `BooGradientText` (gradient-clipped text) build on top of this.
 *
 * The returned object only sets the gradient + transitions; the consumer adds
 * its own `animation` (e.g. `boo-logo` for the position sweep, plus
 * `boo-blob` for the blob shape) and any clip/text rules.
 */
export function useBooGradientStyle(): CSSProperties {
  const [palette, setPalette] = useState<readonly string[]>(() => {
    const a = pick();
    const b = pick([a]);
    const c = pick([a, b]);
    return [a, b, c];
  });

  useEffect(() => {
    const id = setInterval(() => {
      setPalette((prev) => [prev[1], prev[2], pick(prev)]);
    }, SWAP_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return {
    "--boo-c1": `var(${palette[0]})`,
    "--boo-c2": `var(${palette[1]})`,
    "--boo-c3": `var(${palette[2]})`,
    backgroundImage:
      "linear-gradient(135deg, var(--boo-c1), var(--boo-c2), var(--boo-c3))",
    backgroundSize: "300% 300%",
    transition:
      "--boo-c1 1.6s ease, --boo-c2 1.6s ease, --boo-c3 1.6s ease",
  } as CSSProperties;
}
